import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package as PackageIcon, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { getMyPackagePurchases } from '../lib/api';
import { CATEGORY_NAMES, getInitials } from '../lib/utils';

const MyPackagesPanel = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyPackagePurchases();
      setItems((res.data || []).filter((p) => (p.sessions_remaining ?? 0) > 0));
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5 flex items-center gap-2 text-zinc-500 text-sm mb-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your packages…
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="bg-[#18181B] border border-[#C0A062]/30 rounded-lg p-5 mb-6" data-testid="my-packages-panel">
      <div className="flex items-center gap-2 mb-4">
        <PackageIcon className="w-5 h-5 text-[#C0A062]" />
        <h2 className="text-lg font-bold text-white">Your packages</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-packages-list">
        {items.map((p) => {
          const total = p.sessions_total || p.package_snapshot?.session_count || 0;
          const remaining = p.sessions_remaining ?? 0;
          const trainer = p.trainer || {};
          return (
            <div
              key={p.id}
              className="p-3 rounded-lg border border-zinc-700 bg-[#0d0d10] flex flex-col gap-2"
              data-testid={`my-package-${p.id}`}
            >
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8 border border-zinc-700">
                  <AvatarImage src={trainer.profile_image} />
                  <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-[10px]">
                    {getInitials(trainer.full_name || 'T')}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{trainer.full_name || 'Trainer'}</p>
                  <p className="text-[11px] text-zinc-500 truncate">
                    {CATEGORY_NAMES[p.package_snapshot?.category] || p.package_snapshot?.category}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">{p.package_snapshot?.name}</span>
                <span className="text-[#C0A062] text-sm font-bold" data-testid={`credits-${p.id}`}>
                  {remaining}/{total} left
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => navigate(`/trainers/${p.trainer_id}`)}
                className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold mt-1"
                data-testid={`book-with-package-${p.id}`}
              >
                Book a session
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">
        Open a trainer&apos;s profile and pick a date, your credit is applied automatically at checkout.
      </p>
    </div>
  );
};

export default MyPackagesPanel;
